import { LayoutOptimizationResult, StatusVariant } from '@consigli/types';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { BsThreeDots } from 'react-icons/bs';

import { ActionButton, Badge } from '@/components';
import { isValidDocumentType, isHTMLDocumentType } from '@/utils/document-check';

import { useResultsContext } from './results-context';

/**
 * Downloads a file from its URL
 */
const downloadResultFile = async (file: LayoutOptimizationResult['file']): Promise<void> => {
  try {
    if (!file.fileUrl) {
      throw new Error('File URL is not available');
    }
    const response = await fetch(file.fileUrl);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const fileBlob = await response.blob();

    const url = window.URL.createObjectURL(fileBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.originalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: unknown) {
    console.error('Error downloading file:', error);
  }
};

const ActionCell = ({ item }: { item: LayoutOptimizationResult }) => {
  const {
    setSelectedItem,
    setIsDetailViewOpen,
    setDeleteItem,
    setEditItem,
    setPreviewFile,
    setPreviewResultId,
  } = useResultsContext();

  const filename = item.file.originalFilename;
  const canPreview = isValidDocumentType(filename);
  const isHTML = isHTMLDocumentType(filename);

  const actions = [
    // Show preview option for valid document types (PDF, images, IFC, HTML) - first option
    ...(canPreview
      ? [
          {
            label: isHTML ? 'Edit HTML' : 'Show',
            onClick: () => {
              setPreviewFile(item.file);
              setPreviewResultId(item.id);
            },
          },
        ]
      : []),
    {
      label: 'View Details',
      onClick: () => {
        setSelectedItem(item);
        setIsDetailViewOpen(true);
      },
    },
    {
      label: 'Download',
      onClick: () => downloadResultFile(item.file),
    },
    {
      label: 'Edit Type',
      onClick: () => setEditItem(item),
    },
    {
      label: 'Delete',
      onClick: () => setDeleteItem(item),
      destructive: true,
    },
  ];

  return <ActionButton icon={<BsThreeDots />} actions={actions} />;
};

export const useResultsTableConfig = () => {
  const columns: ColumnDef<LayoutOptimizationResult>[] = useMemo(
    () => [
      {
        id: 'filename',
        header: 'Filename',
        accessorFn: (row) => row.file.originalFilename,
        cell: ({ row }) => (
          <span className="font-medium text-gray-900">{row.original.file.originalFilename}</span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        accessorFn: (row) => row.type,
        cell: ({ row }) => <Badge variant={StatusVariant.INFO} text={row.original.type} />,
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorFn: (row) => row.createdAt,
        cell: ({ row }) => (
          <span className="text-gray-600">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => <ActionCell item={row.original} />,
      },
    ],
    [],
  );

  return columns;
};
